import { useMemo } from "react";
import type { Address } from "@guestbook/shared";
import { cn } from "@/shared/lib/utils";
import { FormField } from "@/shared/components/form/FormField";
import { ComboboxFormField } from "@/shared/components/form/ComboboxFormField";
import type { ComboboxOption } from "@/shared/components/ui/Combobox";
import { useCountries, useCountrySubdivisions } from "@/shared/lib/geo";

/**
 * Composed address-input block, wired to the shared `Address` shape
 * (countryCode/countryName/stateCode/stateName — docs/schema.md). Country is a
 * searchable picker fed by the unauthenticated /geo endpoints; the State field
 * becomes a picker when the chosen country has subdivisions and falls back to a
 * free-text input otherwise. The datasets live server-side so they never reach
 * the client bundle; the backend resolver canonicalizes whatever is saved.
 */
export interface AddressFieldsProps {
  value: Address;
  onChange: (next: Address) => void;
  className?: string;
  /** Override the heading shown above the block. Pass null to hide it. */
  heading?: string | null;
}

/**
 * Keeps a currently-selected value visible even when it isn't in the fetched
 * list yet (still loading) or at all (e.g. a code an import stored that isn't a
 * standard option), by prepending a synthetic option from the stored name.
 */
function withSelected(
  options: ComboboxOption[],
  value: string | null | undefined,
  label: string | null | undefined,
): ComboboxOption[] {
  if (!value || options.some((o) => o.value === value)) return options;
  return [{ value, label: label || value }, ...options];
}

export function AddressFields({
  value,
  onChange,
  className,
  heading = "Mailing address",
}: AddressFieldsProps) {
  function set<K extends keyof Address>(key: K, v: Address[K]) {
    onChange({ ...value, [key]: v });
  }

  const { data: countriesData } = useCountries();
  const countryOptions = useMemo<ComboboxOption[]>(() => {
    const base = (countriesData?.items ?? []).map((c) => ({
      value: c.code,
      label: c.name,
    }));
    return withSelected(base, value.countryCode, value.countryName);
  }, [countriesData, value.countryCode, value.countryName]);

  const { data: subsData } = useCountrySubdivisions(value.countryCode);
  const subdivisions = subsData?.items ?? [];
  const hasSubdivisions = subdivisions.length > 0;
  const stateOptions = useMemo<ComboboxOption[]>(() => {
    const base = subdivisions.map((s) => ({ value: s.code, label: s.name }));
    return withSelected(base, value.stateCode, value.stateName);
  }, [subdivisions, value.stateCode, value.stateName]);

  // Selecting a country canonicalizes code+name and clears any prior state,
  // which no longer belongs to the new country.
  function selectCountry(code: string) {
    const name = countryOptions.find((o) => o.value === code)?.label ?? null;
    onChange({
      ...value,
      countryCode: code || null,
      countryName: name,
      stateCode: null,
      stateName: null,
    });
  }

  function selectState(code: string) {
    const name = stateOptions.find((o) => o.value === code)?.label ?? null;
    onChange({ ...value, stateCode: code || null, stateName: name });
  }

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      {heading !== null ? (
        <h2 className="type-heading-sm text-text">{heading}</h2>
      ) : null}
      <FormField
        label="Street address"
        value={value.line1 ?? ""}
        onChange={(e) => set("line1", e.target.value || null)}
        autoComplete="address-line1"
      />
      <FormField
        label="Apartment, suite, etc."
        value={value.line2 ?? ""}
        onChange={(e) => set("line2", e.target.value || null)}
        autoComplete="address-line2"
      />
      <ComboboxFormField
        label="Country"
        placeholder="Choose a country"
        searchPlaceholder="Search countries…"
        emptyText="No matching countries"
        options={countryOptions}
        value={value.countryCode ?? undefined}
        onValueChange={selectCountry}
      />
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <FormField
          label="City"
          value={value.city ?? ""}
          onChange={(e) => set("city", e.target.value || null)}
          autoComplete="address-level2"
        />
        {hasSubdivisions ? (
          <ComboboxFormField
            label="State / province"
            placeholder="Choose a state"
            searchPlaceholder="Search states…"
            emptyText="No matching states"
            options={stateOptions}
            value={value.stateCode ?? undefined}
            onValueChange={selectState}
          />
        ) : (
          <FormField
            label="State / region"
            value={value.stateName ?? ""}
            onChange={(e) => set("stateName", e.target.value || null)}
            autoComplete="address-level1"
          />
        )}
      </div>
      <FormField
        label="ZIP"
        value={value.postalCode ?? ""}
        onChange={(e) => set("postalCode", e.target.value || null)}
        autoComplete="postal-code"
        className="sm:max-w-[50%]"
      />
    </div>
  );
}
